<template>
  <BaseModal :title="'Edit Storage'" @close="closeModal">
    <template #modalContent>
      <div class="modal-content-wrapper">
        <div>
          <h3 class="modal-heading text-xl font-bold text-white p-2">Update storage configurations</h3>
        </div>
        <div class="flex flex-col items-start justify-start h-full w-full">

          <div v-if="storage.name" class="storage-cont bg-[#272727] text-white-lg p-6 w-full max-w- mt-4">
            <div class="provider-label">
              <img :src="storageDetails.provider.image" alt="provider logo" />
              <span class="ml-4">{{ storageDetails.provider.name }}</span>
            </div>

            <div class="storage-label flex flex-col mt-4">
              <span class="stor-name">{{ storageDetails.name }}</span>
              <span class="stor-type"><label>Storage Type: </label>{{ storageDetails.provider.type }}</span>
            </div>

            <div class="toggle-row">
              <span>Storage</span>
              <div class="toggle-wrapper">
                <span class="toggle-label mr-2" :class="{ enabled: isToggleEnable }">
                  {{ isToggleEnable ? "Enabled" : "Disabled" }}
                </span>
                <label class="toggle-switch">
                  <input type="checkbox" v-model="isToggleEnable" @change="handleToggleChange" />
                  <span class="slider"></span>
                </label>
              </div>
            </div>
          </div>

          <!-- Custom Dropdown for Provider Selection -->
          <div v-if="showNewStorageOption" class="relative w-full mt-4 mb-4">
            <label class="block text-sm font-medium mb-4 note-label">
              <!-- Note: You have disabled <span>"{{ storageDetails.name }}"</span> Please select any other one storage
              enabled to continue -->
              ⚠️ You're about to disable the storage: <span>"{{ storageDetails.name }}"</span>
              <br></br>
              Please select a new storage from the list below. This will be assigned to all the affected cast(s) currently using <span>{{ storageDetails.name }}</span>.
            </label>
            <div @click="toggleDropdown" style="border:1px solid white !important;"
              class="h-12 w-full p-2 text-lg bg-black text-white flex justify-between items-center cursor-pointer">
              <div class="flex gap-4 items-center justify-start">
                <img v-if="selectedStorage" :src="selectedStorage.provider.image" class="w-8 h-8 object-contain"
                  alt="icon" />
                <span><span>{{ selectedStorage ? selectedStorage.name : 'Select Provider' }}</span> -
                  <span class="capitalize">{{ selectedStorage ? selectedStorage.provider.type : 'Select Provider'
                  }}</span></span>
              </div>
              <span>
                <DownButton />
              </span>
            </div>

            <div v-if="isOpen" class="absolute w-full bg-dark-black text-white z-10 mt-1"
              style="border:1px solid white !important;">
              <div v-for="storage in storageOptions" :key="storage.id"
                @click="!storage.deActive && selectStorage(storage)"
                class="p-2 px-4 h-12 flex gap-4 items-center justify-start text-lg" :class="[
                  storage.deActive
                    ? 'opacity-50 cursor-not-allowed'
                    : 'cursor-pointer hover:bg-gray-800 hover:text-green-500'
                ]">
                <img class="w-6 h-6 object-contain" :src="storage.provider.image" />
                <span>
                  <span>{{ storage.name }}</span> -
                  <span class="capitalize">{{ storage.provider.type }}</span>
                  <span v-if="storage.deActive">(Disabled)</span>
                </span>
              </div>
            </div>
          </div>

          <!-- Next button -->
          <div class="flex justify-end gap-2 w-full mt-4">
            <button @click="handleDisableButtonClick"
              class="update-btn bg-transparent outline-none border-white cursor-pointer text-primary  py-2 px-4 hover:bg-[#b0b81c]">
              Update configuration
            </button>
          </div>
        </div>
      </div>
    </template>
  </BaseModal>
</template>

<script>
import BaseModal from "@/components/common/BaseModal.vue";
import api from '@/http/api/api.js';
import DownButton from "../../../new-dashboard/decast/components/common/DownButton.vue";
import constants from "../../../../../constant";

export default {
  name: 'DisableStorageModal',
  props: {
    closeModal: {
      type: Function,
      required: true,
    },
    storage: {
      type: Object,
      required: true,
    },
    fetchStorages: {
      type: Function,
      required: true,
    }
  },
  components: {
    BaseModal,
    DownButton
  },
  data() {
    return {
      isLoading: false,
      storageDetails: {},
      selectedStorage: {},
      isToggleEnable: false,
      storageOptions: [],
      isOpen: false,
      showNewStorageOption: false,
      isCastStorageMappingLoaded: false,
      castStorageMapping: [],
      isProcessing: false,
    };
  },
  async mounted() {
    document.getElementById('loading-bg-transparent').style.display = 'none';
    this.storageDetails = { ...this.storage };
    this.isToggleEnable = !this.storageDetails.deActive;
    this.getDropDownStorages();
    this.getDecastList();
  },
  methods: {
    handleToggleChange() {
      if (!this.isToggleEnable) {
        this.showNewStorageOption = true;
      } else {
        this.showNewStorageOption = false;
      }
    },
    toggleDropdown() {
      this.isOpen = !this.isOpen;
    },
    selectStorage(storage) {
      this.selectedStorage = storage;
      this.isOpen = false;
    },
    async saveStorage() {
      if (!this.isFormValid) {
        this.$vs.notify({
          title: 'Error',
          text: 'Please complete the form before saving!',
          color: 'danger',
        });
        return;
      }

      if (this.form.selfHost && !this.isValidated) {
        this.$vs.notify({
          title: 'Error',
          text: 'Please validate the connection before saving!',
          color: 'warning',
        });
        return;
      }

      const provider = this.providers.find((p) => p.id === this.form.selectedProviderId);

      try {
        // Send PATCH request to update storage
        await api.patch(`${constants.apiCastUrl}/api/v1/storageproxy/storages/${this.form.id}`, {
          name: this.form.name,
          provider: {
            "id": this.form.selectedProviderId
          },
          options: this.form.selfHost ? this.form.options : {},
          selfHost: this.form.selfHost,
          deActive: this.form.deActive,
          status: this.form.selfHost ? this.isValidated : false,
        });
        this.fetchStorages();
        this.$vs.notify({
          title: 'Success',
          text: 'Storage updated successfully!',
          color: 'success',
        });

        this.closeModal();
      } catch (error) {
        console.error('Error updating storage:', error);
        this.$vs.notify({
          title: 'Error',
          text: 'Failed to update storage. Please try again.',
          color: 'danger',
        });
      }
    },
    async getDropDownStorages() {
      this.isLoading = true;
      try {
        const storagesResponse = await api.get(`${constants.apiCastUrl}/api/v1/storageproxy/storages`);
        this.storageOptions = storagesResponse.data.data.filter(storage => storage.id !== this.storage.id);
        console.log("this.storageOptions", this.storageOptions)
        this.selectedStorage = this.storageOptions[0];
      } catch (error) {
        console.error('Error fetching storages:', error);
        this.$vs.notify({
          title: 'Error',
          text: 'Failed to load storages. Please try again.',
          color: 'danger',
        });
      } finally {
        this.isLoading = false;
      }
    },
    async handleDisableButtonClick() {
      if (this.isToggleEnable) {
        document.getElementById('loading-bg-transparent-title').innerText = '🔓 Enabling storage';
        await this.updateStorageStatus(false);
        this.fetchStorages();
        this.$vs.notify({
          title: 'Success',
          text: 'Storage enabled successfully!',
          color: 'success',
        });
        document.getElementById('loading-bg-transparent').style.display = 'none';
        return;
      }
      try {
        document.getElementById('loading-bg-transparent-title').innerText = '🚀 Assigning new storage to cast';
        document.getElementById('loading-bg-transparent').style.display = 'flex';
        this.isProcessing = true;

        let retries = 0;
        const maxRetries = 600; // 600 * 100ms = 60,000ms = 1 minute

        while (!this.isCastStorageMappingLoaded && retries < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 100)); // wait 100ms
          retries++;
        }

        if (!this.isCastStorageMappingLoaded) {
          this.isProcessing = false;
          console.warn("castStorageMapping not ready after waiting");
          return;
        }
        const castToUpdateStorage = this.castStorageMapping.filter(item => item.storage_id === this.storage.id);
        if (castToUpdateStorage.length !== 0) {
          await Promise.all(
            castToUpdateStorage.map(cast => {
              const storageParams = {
                cast_id: cast.cast_id,
                storage_id: this.selectedStorage.id
              };
              return api.post(`${constants.apiCastUrl}/api/v1/decast/storageid/`, storageParams);
            })
          );
        }
        document.getElementById('loading-bg-transparent-title').innerText = '🔒 Disabling storage';
        await this.updateStorageStatus(true);
        this.fetchStorages();
        this.$vs.notify({
          title: 'Success',
          text: 'Storage disabled successfully!',
          color: 'success',
        });
      } catch (error) {
        this.$vs.notify({
          title: 'Error',
          text: 'Failed to disable storage. Please try again.',
          color: 'danger',
        });
      } finally {
        document.getElementById('loading-bg-transparent').style.display = 'none';
      }
    },
    async updateStorageStatus(status) {
      await api.patch(`${constants.apiCastUrl}/api/v1/storageproxy/storages/${this.storage.id}`, {
        deActive: status,
      });
    },
    async getDecastList() {
      this.isCastStorageMappingLoaded = false;
      try {
        const response = await this.$store.dispatch('cast/getDecasts');

        if (response.data.my_events) {
          const allEvents = response.data.my_events.sort((a, b) => b.event_id - a.event_id);
          // Fetch storage IDs for each event
          const storageIdPromises = allEvents.map(async (event) => {
            try {
              const storageIdResponse = await api.get(
                `${constants.apiCastUrl}/api/v1/decast/storageid/?cast_id=${event.public_meeting_id}`
              );
              return {
                cast_id: event.public_meeting_id,
                storage_id: storageIdResponse.data.current_storage_id
              };
            } catch (err) {
              console.error(`Error fetching storage ID for cast ${event.public_meeting_id}:`, err);
              return null;
            }
          });

          const storageIdList = (await Promise.all(storageIdPromises)).filter(Boolean); // remove nulls
          this.castStorageMapping = storageIdList; // <- add this to your data() if not already there
        }
      } catch (error) {
        console.error("Error in getDecastList:", error);
      } finally {
        this.isCastStorageMappingLoaded = true;
      }
    },
  },
};
</script>

<style scoped>
input,
select {
  border: 1px solid #333333;
}

input:focus,
select:focus {
  outline: none;
  border-color: #d7df23;
}

.storage-cont {
  border-top: 1px solid #FFFFFF;
  border-left: 1px solid #FFFFFF;
  border-right: 3px solid #FFFFFF;
  border-bottom: 3px solid #FFFFFF;
}

.provider-label {
  display: flex;
  align-items: center;
}

.provider-label img {
  height: 24px;
}

.provider-label span {
  font-size: 22px;
}

.stor-name {
  font-size: 18px;
}

.stor-type {
  font-size: 16px;
}

.stor-type label {
  color: #545454;
}

.note-label {
  color: #4C84B6;
}

.note-label span {
  color: #D7DF23;
}

.update-btn:disabled {
  cursor: not-allowed;
  opacity: 0.8;
}

/* Toggle */
.toggle-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid #444;
  padding-top: 12px;
  margin-top: 12px;
}

.toggle-wrapper {
  display: flex;
  align-items: center;
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 46px;
  height: 24px;
  margin-right: 8px;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #444;
  transition: 0.4s;
  border-radius: 24px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: 0.4s;
  border-radius: 50%;
}

.toggle-switch input:checked+.slider {
  background-color: #22c55e;
}

.toggle-switch input:checked+.slider:before {
  transform: translateX(22px);
}

.toggle-label {
  font-size: 12px;
  color: #aaa;
}

.toggle-label.enabled {
  color: #22c55e;
}
</style>